import { useEffect, useState } from 'react';

import { Accordion } from '@equip.health/ui';

import BillingPeriodAccordionFooter from '~/components/common/billingPeriods/AccordionFooter';
import { RenderMode } from '~/components/common/billingPeriods/types';
import {
  initialBillingPeriodState,
  useBillingPeriod,
} from '~/components/common/billingPeriods/useBillingPeriod';
import {
  ACCORDION_HEIGHT_CALCULATED,
  ACCORDION_HEIGHT_CALCULATED_FULLSCREEN,
  ACCORDION_WIDTH_LARGE,
  BILLING_PERIODS_TOP_LEVEL_ID,
} from '~/lib/constants';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';

import BillingPeriodCreate from './BillingPeriodCreate';
import BillingPeriodView from './BillingPeriodView';

const VIEW_TITLE = 'View billing period';
const ADD_TITLE = 'Add billing period';
const EDIT_TITLE = 'Edit billing period';

export interface BillingPeriodAccordionProps {
  currentBillingPeriod: BillingPeriod;
  dismissBillingPeriod: () => void;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
}

const BillingPeriodAccordion = ({
  currentBillingPeriod,
  dismissBillingPeriod,
  isExpanded,
  setIsExpanded,
}: BillingPeriodAccordionProps) => {
  const { permissions } = useGlobalContext();
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const useBillingPeriodState = useBillingPeriod();
  const {
    renderMode,
    setBillingPeriod,
    initializeBillingPeriodState,
    resetBillingPeriodState,
  } = useBillingPeriodState;

  useEffect(() => {
    if (!currentBillingPeriod?.billingPeriodId) {
      initializeBillingPeriodState(initialBillingPeriodState);
    } else {
      setBillingPeriod(currentBillingPeriod);
    }
    return () => {
      resetBillingPeriodState();
    };
  }, [
    initializeBillingPeriodState,
    currentBillingPeriod,
    resetBillingPeriodState,
    setBillingPeriod,
  ]);

  const viewRenderer = () => {
    if (renderMode === RenderMode.CREATE || renderMode === RenderMode.EDIT) {
      return <BillingPeriodCreate billingPeriodState={useBillingPeriodState} />;
    }
    return <BillingPeriodView billingPeriodState={useBillingPeriodState} />;
  };

  let accordionTitle = EDIT_TITLE;
  if (renderMode === RenderMode.VIEW) {
    accordionTitle = VIEW_TITLE;
  } else if (renderMode === RenderMode.CREATE) {
    accordionTitle = ADD_TITLE;
  }

  return (
    <Accordion
      dismissMenu={dismissBillingPeriod}
      expandMax
      footer={
        permissions[MaudPermissions.MAUD_CARE_PLAN] && (
          <BillingPeriodAccordionFooter
            billingPeriodState={useBillingPeriodState}
          />
        )
      }
      id={`${BILLING_PERIODS_TOP_LEVEL_ID}__accordion`}
      isExpanded={isExpanded}
      isFullScreen={isFullScreen}
      panelContentMaxHeight={
        isFullScreen
          ? ACCORDION_HEIGHT_CALCULATED_FULLSCREEN
          : ACCORDION_HEIGHT_CALCULATED
      }
      panelMaxWidth={isFullScreen ? '100%' : ACCORDION_WIDTH_LARGE}
      panelMinWidth={ACCORDION_WIDTH_LARGE}
      setIsExpanded={setIsExpanded}
      setIsFullScreen={setIsFullScreen}
      title={accordionTitle}
    >
      {viewRenderer()}
    </Accordion>
  );
};

export default BillingPeriodAccordion;
